// frontend/src/components/QuickIssueReport.js
import React, { useState } from 'react';
import { AlertCircle, Loader2, X, Zap, AlertTriangle, Info } from 'lucide-react';
import { vehicleHealthAPI, vehicleProfileAPI, apiUtils } from '../services/api';

const QuickIssueReport = ({ vehicleId, currentMileage, onSuccess, onCancel }) => {
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (description.length < 10) {
      setError('Please provide more details about the issue (at least 10 characters)');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Create a title from the description (first 50 characters)
      const title = description.length > 50 ? description.substring(0, 50) + '...' : description;
      
      const result = await vehicleHealthAPI.createVehicleIssue({
        vehicle_profile_id: vehicleId,
        title: title,
        description: description,
        severity: 'medium', // Default severity
        current_mileage: currentMileage
      });

      console.log('Vehicle issue reported:', result);
      
      // Show success message
      setSuccess(true);
      
      // Show AI insights if available
      if (result.ai_diagnosis) {
        setAiResult(result.ai_diagnosis);
        setTimeout(() => {
          if (onSuccess) onSuccess(result);
        }, 3000); // Give user time to see the AI analysis
      } else {
        // Auto-close after 2 seconds if no AI analysis
        setTimeout(() => {
          if (onSuccess) onSuccess(result);
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to report vehicle issue:', err);
      setError('Failed to report vehicle issue. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'critical': 'bg-danger-tint text-danger border-danger-border',
      'major': 'bg-warning-tint text-warning border-warning-border',
      'moderate': 'bg-warning-tint text-warning border-warning-border',
      'minor': 'bg-info-tint text-info border-info-border'
    };
    return colors[severity] || 'bg-surface-muted text-ink-soft border-border-default';
  };

  const getSafetyIcon = (impact) => {
    switch (impact) {
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-danger" />;
      case 'medium':
        return <AlertCircle className="w-5 h-5 text-warning" />;
      default:
        return <Info className="w-5 h-5 text-accent" />;
    }
  };

  if (aiResult) {
    return (
      <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-ink-soft">AI Analysis Complete</h3>
          <button onClick={() => onSuccess && onSuccess()} className="text-ink-muted hover:text-ink-muted">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Severity and Urgency */}
          <div className="flex items-center space-x-4">
            <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getSeverityColor(aiResult.severity_assessment)}`}>
              {aiResult.severity_assessment.toUpperCase()}
            </div>
            <div className="flex items-center text-sm text-ink-muted">
              <Zap className="w-4 h-4 mr-1" />
              Urgency: {aiResult.urgency_score}/10
            </div>
            <div className="flex items-center text-sm text-ink-muted">
              {getSafetyIcon(aiResult.safety_impact)}
              <span className="ml-1">Safety: {aiResult.safety_impact}</span>
            </div>
          </div>

          {/* Potential Causes */}
          <div>
            <h4 className="font-medium text-ink-soft mb-2">Potential Causes:</h4>
            <ul className="list-disc list-inside space-y-1">
              {aiResult.potential_causes.map((cause, idx) => (
                <li key={idx} className="text-sm text-ink-soft">{cause}</li>
              ))}
            </ul>
          </div>

          {/* Recommended Actions */}
          <div>
            <h4 className="font-medium text-ink-soft mb-2">Recommended Actions:</h4>
            <ul className="list-disc list-inside space-y-1">
              {aiResult.recommended_actions.map((action, idx) => (
                <li key={idx} className="text-sm text-ink-soft">{action}</li>
              ))}
            </ul>
          </div>

          {/* Cost Estimate */}
          {aiResult.estimated_cost_range && (
            <div className="bg-surface-page rounded-lg p-3">
              <p className="text-sm font-medium text-ink-soft">Estimated Cost:</p>
              <p className="text-lg font-semibold text-ink-soft">
                ${aiResult.estimated_cost_range.min} - ${aiResult.estimated_cost_range.max}
              </p>
            </div>
          )}

          {/* Related Systems */}
          {aiResult.related_systems && aiResult.related_systems.length > 0 && (
            <div>
              <p className="text-sm font-medium text-ink-soft mb-1">Related Systems:</p>
              <div className="flex flex-wrap gap-2">
                {aiResult.related_systems.map((system, idx) => (
                  <span key={idx} className="px-2 py-1 bg-surface-muted text-ink-soft text-small rounded">
                    {system}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-ink-muted mb-2">Issue has been logged to your maintenance history</p>
          <button
            onClick={() => onSuccess && onSuccess()}
            className="text-accent hover:text-accent-hover font-medium"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Show success notification
  if (success) {
    return (
      <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-success-tint mb-4">
            <svg className="h-6 w-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-ink-soft mb-2">Issue Reported Successfully!</h3>
          <p className="text-sm text-ink-muted mb-4">
            Your vehicle issue has been logged and saved to the database.
          </p>
          <button
            onClick={() => onSuccess && onSuccess()}
            className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success/80"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border border-border-default p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-ink-soft">Report an Issue</h3>
        {onCancel && (
          <button onClick={onCancel} className="text-ink-muted hover:text-ink-muted">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-ink-soft mb-2">
            Describe the issue
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Example: Hearing a grinding noise when braking, especially when turning left. Started about a week ago and seems to be getting worse."
            className="w-full px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
            rows={4}
            required
            minLength={10}
          />
          <p className="mt-1 text-small text-ink-muted">
            Be specific - our AI will analyze this to provide recommendations
          </p>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-ink-soft mb-2">
            Current Mileage
          </label>
          <input
            type="number"
            value={currentMileage}
            readOnly
            className="w-full px-3 py-2 border border-border-strong rounded-lg bg-surface-page"
          />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-danger-tint border border-danger-border rounded-lg">
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-ink-soft bg-surface-card border border-border-default rounded-lg hover:bg-surface-page"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={submitting || description.length < 10}
            className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              'Report Issue'
            )}
          </button>
        </div>
      </form>

      <div className="mt-4 p-3 bg-info-tint border border-info-border rounded-lg">
        <p className="text-small text-info">
          <strong>AI-Powered Analysis:</strong> Our system will analyze your description to determine severity, 
          identify potential causes, and provide actionable recommendations.
        </p>
      </div>
    </div>
  );
};

export default QuickIssueReport;